"use client";

import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import type {
  CardGridWithFilters,
  Article,
  ArticleType,
} from "@/generated/sb";
import type { TextHeader } from "@/generated/sb";
import { useArticles } from "@/hooks/useArticles";
import { useGlobalTranslations } from "@/hooks/useGlobalTranslations";
import { PrefetchedArticlesData, FilterGroupData } from "@/types/articles";
import { ISbStoryData } from "storyblok-js-client";
import {
  CardGridWithFilters as CardGridWithFiltersUI,
  type ArticleData,
} from "@repo/ui-parkmobile/sections/CardGridWithFilters";
import {
  FilterSection,
  FilterSectionMobile,
  type FilterField,
} from "@repo/ui-parkmobile";
import { articleResolver } from "@/lib/storyblok/adaptors";
import {
  trackArticleClick,
  trackFilterSelect,
  trackResourceSearch,
} from "@/utils/trackingScripts";
import { deleteAutoSlug } from "@/utils/autoSlug";
import { useSearchParams, usePathname, useRouter } from "next/navigation";

export type CardGridWithFiltersClientProps = {
  blok: CardGridWithFilters & {
    lastTextHeader?: TextHeader;
    prefetchedArticlesData?: PrefetchedArticlesData;
  };
  initialArticles: ArticleData[];
  initialPage: number;
  initialTotalPages: number;
};

export function CardGridWithFiltersClient({
  blok,
  initialArticles,
  initialPage,
  initialTotalPages,
}: CardGridWithFiltersClientProps) {
  const [isClientMode, setIsClientMode] = useState(false);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const initialSearch = searchParams.get("search") || "";
  const [localSearchValue, setLocalSearchValue] = useState(initialSearch);
  const currentSearch = searchParams.get("search") || "";

  useEffect(() => {
    if (!searchDebounceRef.current) {
      setLocalSearchValue(currentSearch);
    }
  }, [currentSearch]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const lastTextHeader = blok.lastTextHeader;
  const sectionName =
    lastTextHeader?.content?.[0]?.title?.[0]?.content || "Resources";

  const prefetchedData = blok.prefetchedArticlesData;
  const filterGroups = prefetchedData?.filters || [];

  const preselectedStories = useMemo(() => {
    return (blok.preselectedArticles || []).filter(
      (article) => typeof article !== "string",
    ) as ISbStoryData<Article>[];
  }, [blok.preselectedArticles]);

  const resolvedArticleTypeUuid = useMemo(() => {
    if (!blok.specificArticleType) return undefined;
    if (typeof blok.specificArticleType === "string")
      return blok.specificArticleType;
    return (blok.specificArticleType as ISbStoryData<ArticleType>)
      .uuid;
  }, [blok.specificArticleType]);

  const currentPage = parseInt(searchParams.get("page") || "1", 10);

  const currentFilters = useMemo(() => {
    const filters: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      if (key !== "page" && key !== "search") {
        filters[key] = value;
      }
    });
    return filters;
  }, [searchParams]);

  const handleFilterChange = useCallback(
    (filterKey: string, selectedTitle: string, selectedValue: string) => {
      setIsClientMode(true);
      trackFilterByKey(filterKey, selectedTitle, sectionName);
    },
    [sectionName],
  );

  const handleSearchChange = useCallback(
    (query: string) => {
      setIsClientMode(true);
      if (query) {
        trackResourceSearch({
          section: sectionName,
          config: "cardGridWithFilters",
          searchTerm: query,
        });
      }
    },
    [sectionName],
  );

  const { articles, filters, searchQuery, onSearchChange, totalPages } =
    useArticles(preselectedStories, {
      specificArticleType: resolvedArticleTypeUuid,
      onFilterChange: handleFilterChange,
      onSearchChange: handleSearchChange,
      prefetchedData,
      currentPage,
      initialFilters: currentFilters,
      initialSearch: currentSearch,
    });

  const translations = useGlobalTranslations(["showMore", "words"]);

  const clientArticles = useMemo(() => {
    const preselectedProps = preselectedStories
      .map((article) => {
        const articleData = articleResolver({
          article,
          imageAspectRatio: "1/1",
          disableDescription: blok.disableDescriptions,
        });
        if (!articleData) return null;
        return {
          id: article.uuid,
          image: articleData.image,
          title: {
            content: articleData.title,
            level: "h6" as const,
            fontSize: "p" as const,
            className: blok.disableDescriptions
              ? "uipm:font-semibold"
              : undefined,
          },
          text: articleData.description,
          date: article.content.publicationDate || article.created_at,
          link: {
            href: `/${deleteAutoSlug(article.full_slug)}`,
            text: translations.readMore || "Read More",
          },
        };
      })
      .filter((a): a is NonNullable<typeof a> => a !== null);

    const fetchedProps = articles
      .map((article) => {
        const articleData = articleResolver({
          article,
          imageAspectRatio: "1/1",
          disableDescription: blok.disableDescriptions,
        });
        if (!articleData) return null;
        return {
          id: article.uuid,
          image: articleData.image,
          title: {
            content: articleData.title,
            level: "h6" as const,
            fontSize: "p" as const,
            className: blok.disableDescriptions
              ? "uipm:font-semibold"
              : undefined,
          },
          text: articleData.description,
          date: article.content.publicationDate || article.created_at,
          link: {
            href: `/${deleteAutoSlug(article.full_slug)}`,
            text: translations.readMore || "Read More",
          },
        };
      })
      .filter((a): a is NonNullable<typeof a> => a !== null);

    return currentPage === 1
      ? [...preselectedProps, ...fetchedProps]
      : fetchedProps;
  }, [
    articles,
    preselectedStories,
    blok.disableDescriptions,
    translations,
    currentPage,
  ]);

  const filterFields = useMemo(() => {
    const fields: FilterField[] = [];
    const hideFilters = blok.hideSpecificFilters || [];

    filterGroups.forEach((group) => {
      if (hideFilters.includes(group.key as (typeof hideFilters)[number]))
        return;
      if (group.options.length > 0) {
        fields.push({
          type: "dropdown",
          name: group.key,
          placeholder: group.label || "Select...",
          options: group.options.map((opt) => ({
            label: opt.title,
            value: opt.uuid,
          })),
        });
      }
    });

    fields.push({
      type: "search",
      name: "search",
      placeholder: blok.searchPlaceholder || "Search articles...",
    });

    return fields;
  }, [filterGroups, blok.hideSpecificFilters, blok.searchPlaceholder]);

  const filterValues = useMemo(() => {
    const values: Record<string, string> = {
      search: localSearchValue,
    };
    filters.forEach((f) => {
      values[f.key] = f.value;
    });
    return values;
  }, [filters, localSearchValue]);

  const handleFieldChange = useCallback(
    (fieldName: string, value: string) => {
      setIsClientMode(true);

      if (fieldName === "search") {
        setLocalSearchValue(value);

        if (searchDebounceRef.current) {
          clearTimeout(searchDebounceRef.current);
        }

        searchDebounceRef.current = setTimeout(() => {
          searchDebounceRef.current = null;

          const params = new URLSearchParams();
          searchParams.forEach((v, k) => {
            if (k !== "page" && k !== "search") {
              params.set(k, v);
            }
          });

          if (value) {
            params.set("search", value);
            trackResourceSearch({
              section: sectionName,
              config: "cardGridWithFilters",
              searchTerm: value,
            });
          }

          onSearchChange(value);

          const newUrl = params.toString()
            ? `${pathname}?${params.toString()}`
            : pathname;
          router.push(newUrl, { scroll: false });
        }, 500);
      } else {
        const params = new URLSearchParams();
        searchParams.forEach((v, k) => {
          if (k !== "page" && k !== fieldName) {
            params.set(k, v);
          }
        });

        const group = filterGroups.find((g) => g.key === fieldName);
        const option = group?.options.find((opt) => opt.uuid === value);
        const title = option?.title || "";

        if (title) {
          params.set(fieldName, title);
          trackFilterByKey(fieldName, title, sectionName);
        }

        const filter = filters.find((f) => f.key === fieldName);
        if (filter?.onValueChange) {
          filter.onValueChange(value);
        }

        const newUrl = params.toString()
          ? `${pathname}?${params.toString()}`
          : pathname;
        router.push(newUrl, { scroll: false });
      }
    },
    [
      filters,
      filterGroups,
      onSearchChange,
      searchParams,
      pathname,
      router,
      sectionName,
    ],
  );

  const handleArticleClick = useCallback(
    (title?: string, date?: string, linkText?: string, linkHref?: string) => {
      trackArticleClick({
        section: sectionName,
        config: "cardGridWithFilters",
        articleTitle: title || "",
        articleDate: date || "",
        ctaText: linkText || "",
        link: linkHref || "",
      });
    },
    [sectionName],
  );

  const buildPageUrl = useCallback(
    (page: number) => {
      const params = new URLSearchParams();
      if (page !== 1) params.set("page", page.toString());
      searchParams.forEach((value, key) => {
        if (key !== "page") params.set(key, value);
      });
      return params.toString() ? `${pathname}?${params.toString()}` : pathname;
    },
    [pathname, searchParams],
  );

  const hasUrlFilters =
    currentSearch ||
    Object.keys(currentFilters).length > 0 ||
    currentPage !== initialPage;
  const shouldShowClientContent = isClientMode || hasUrlFilters;

  const displayArticles = shouldShowClientContent
    ? clientArticles
    : initialArticles;
  const displayTotalPages = shouldShowClientContent
    ? totalPages
    : initialTotalPages;

  const filtersSlot = (
    <>
      <div className="uipm:block uipm:md:hidden uipm:sticky uipm:top-[60px] uipm:z-40">
        <FilterSectionMobile
          fields={filterFields}
          inputBackground="default"
          onFieldChange={handleFieldChange}
          initialValues={filterValues}
          showBorder={false}
        />
      </div>
      <div className="uipm:hidden uipm:md:block uipm:max-w-[1440px] uipm:mx-auto uipm:px-[var(--container-margin)]">
        <FilterSection
          fields={filterFields}
          backgroundColor="none"
          topPadding="default"
          bottomPadding="default"
          inputBackground="default"
          onFieldChange={handleFieldChange}
          initialValues={filterValues}
        />
      </div>
    </>
  );

  return (
    <CardGridWithFiltersUI
      backgroundColor={blok.backgroundColor || "none"}
      topPadding={blok.topPadding || "none"}
      bottomPadding={blok.bottomPadding || "none"}
      cardsBackground={(blok.cardsBackground as any) || "none"}
      articles={displayArticles}
      currentPage={currentPage}
      totalPages={displayTotalPages}
      buildPageUrl={buildPageUrl}
      id={blok.anchor}
      onArticleClick={shouldShowClientContent ? handleArticleClick : undefined}
      filtersSlot={filtersSlot}
    />
  );
}

function trackFilterByKey(
  filterKey: string,
  selectedTitle: string,
  sectionName: string,
) {
  const events: Record<string, { event: string; filterType: string }> = {
    region: { event: "regionFilterSelect", filterType: "selectedRegion" },
    industry: { event: "industryFilterSelect", filterType: "selectedIndustry" },
    topic: { event: "topicFilterSelect", filterType: "selectedTopic" },
    type: { event: "typeFilterSelect", filterType: "selectedType" },
    when: { event: "dateFilterSelect", filterType: "selectedDateRange" },
  };

  const tracking = events[filterKey.toLowerCase()];
  if (tracking) {
    trackFilterSelect({
      event: tracking.event,
      section: sectionName,
      config: "cardGridWithFilters",
      filterType: tracking.filterType,
      selectedValue: selectedTitle,
    });
  }
}
